import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, Check, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { LeadFormBgSlides } from '../components/LeadFormBgSlides';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const darken = (hex, pct) => {
  const n = parseInt(hex.replace('#',''), 16);
  const r = Math.max(0, (n >> 16) - Math.round(255 * pct));
  const g = Math.max(0, ((n >> 8) & 0xff) - Math.round(255 * pct));
  const b = Math.max(0, (n & 0xff) - Math.round(255 * pct));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6,'0')}`;
};

const LeadForm = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('intro');
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentValue, setCurrentValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { loadForm(); }, [slug]);
  useEffect(() => {
    if (form && phase === 'questions' && form.questions[currentStep]) {
      setCurrentValue(answers[form.questions[currentStep].id] || '');
      setError('');
    }
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [currentStep, phase, form]);
  useEffect(() => {
    if (form) {
      const s = localStorage.getItem(`lf_${form.id}`);
      if (s) { try { const d = JSON.parse(s); if (d.answers) setAnswers(d.answers); if (d.step !== undefined && d.step < form.questions.length) { setCurrentStep(d.step); setPhase('questions'); } } catch {} }
    }
  }, [form]);
  useEffect(() => {
    if (form && phase === 'questions' && Object.keys(answers).length > 0)
      localStorage.setItem(`lf_${form.id}`, JSON.stringify({ answers, step: currentStep }));
  }, [answers, currentStep, form, phase]);

  const loadForm = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/lead-forms/slug/${slug}`); setForm(r.data); }
    catch { setForm(null); } finally { setLoading(false); }
  };

  const validatePhone = (v) => { const d = v.replace(/\D/g, ''); return d.length >= 10 && d.length <= 11; };
  const formatPhone = (v) => {
    const d = v.replace(/\D/g, '');
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 7) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`;
  };

  const handleNext = async () => {
    const q = form.questions[currentStep]; const val = currentValue.trim();
    if (!val) { setError('Por favor, responda esta pergunta'); return; }
    if (q.type === 'phone' && !validatePhone(val)) { setError('Número inválido'); return; }
    const na = { ...answers, [q.id]: val }; setAnswers(na);
    if (currentStep < form.questions.length - 1) setCurrentStep(p => p + 1);
    else await submitForm(na);
  };

  const submitForm = async (fa) => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/lead-forms/responses`, {
        form_id: form.id, answers: Object.entries(fa).map(([question_id, answer]) => ({ question_id, answer }))
      });
      localStorage.removeItem(`lf_${form.id}`); setPhase('success');
    } catch { setError('Erro ao enviar. Tente novamente.'); } finally { setSubmitting(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleNext(); } };

  const getBackgrounds = () => {
    if (!form) return [];
    if (form.use_package_background !== false && form.resolved_background_url) return [form.resolved_background_url];
    if (form.background_urls?.length) return form.background_urls;
    return form.background_url ? [form.background_url] : [];
  };

  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-white/40 animate-spin" /></div>;
  if (!form) return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white/50 text-xl font-light">Formulário não encontrado</div>;

  // Dynamic colors
  const C = form.accent_color || '#007BFF';
  const Chover = darken(C, -0.08);
  const Cglow = C + '30';
  const Cfinal = form.final_button_color || C;
  const CfinalHover = darken(Cfinal, -0.08);
  const CfinalGlow = Cfinal + '30';

  const bgUrls = getBackgrounds();
  const bgLayer = <LeadFormBgSlides urls={bgUrls} />;
  const totalQ = form.questions?.length || 0;
  const progress = phase === 'success' ? 100 : phase === 'intro' ? 0 : Math.round((currentStep / totalQ) * 100);

  const BrandBtn = ({ children, onClick, href, color = C, hoverColor = Chover, glow = Cglow, className = '', ...props }) => {
    const cls = `inline-flex items-center justify-center gap-3 text-white font-bold text-lg px-12 py-5 rounded-2xl transition-all duration-200 hover:scale-[1.03] ${className}`;
    const style = { backgroundColor: color, boxShadow: `0 10px 28px ${glow}` };
    const handlers = {
      onMouseEnter: (e) => e.currentTarget.style.backgroundColor = hoverColor,
      onMouseLeave: (e) => e.currentTarget.style.backgroundColor = color,
    };
    if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} {...handlers} {...props}>{children}</a>;
    return <button onClick={onClick} className={cls} style={style} {...handlers} {...props}>{children}</button>;
  };

  // ===================== INTRO =====================
  if (phase === 'intro') return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-gray-950">
      {bgLayer}
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black/90" />
      <div className="flex-1 flex items-center justify-center relative z-10 px-6 py-12">
        <div className="text-center max-w-lg mx-auto animate-[fadeUp_0.6s_ease-out]">
          {form.logo_url && <img src={form.logo_url} alt="" className="h-14 mx-auto mb-8 object-contain drop-shadow-lg" />}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-5 leading-[1.12] tracking-tight">
            {form.intro_title || form.title}
          </h1>
          {form.intro_subtitle && <p className="text-base sm:text-lg text-white/65 mb-8 font-light leading-relaxed">{form.intro_subtitle}</p>}
          {form.intro_benefits?.length > 0 && (
            <div className="space-y-3.5 mb-10 text-left max-w-sm mx-auto">
              {form.intro_benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-3.5">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C + '22' }}>
                    <Check className="w-4 h-4" style={{ color: C }} />
                  </div>
                  <span className="text-white/90 text-[15px]">{b}</span>
                </div>
              ))}
            </div>
          )}
          <BrandBtn onClick={() => setPhase('questions')} data-testid="intro-start-btn">
            {form.intro_button_text || 'Iniciar'} <ArrowRight className="w-5 h-5" />
          </BrandBtn>
          {form.intro_footer_text && <p className="text-sm text-white/30 mt-6 font-light">{form.intro_footer_text}</p>}
        </div>
      </div>
      <ProgressBar value={0} color={C} />
      <Style />
    </div>
  );

  // ===================== SUCESSO =====================
  if (phase === 'success') return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-gray-950">
      {bgLayer}
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black/90" />
      <div className="flex-1 flex items-center justify-center relative z-10 px-6">
        <div className="text-center max-w-lg mx-auto animate-[fadeUp_0.6s_ease-out]">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8" style={{ backgroundColor: Cfinal + '22' }}>
            <Check className="w-10 h-10" style={{ color: Cfinal }} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
            {form.success_title || 'Recebemos suas informações!'}
          </h1>
          <p className="text-lg text-white/55 mb-10 font-light">
            {form.success_message || 'Você agora está na lista prioritária.'}
          </p>
          {form.final_button_url && (
            <BrandBtn href={form.final_button_url} color={Cfinal} hoverColor={CfinalHover} glow={CfinalGlow} data-testid="final-cta-button">
              {form.final_button_text || 'Continuar'} <ArrowRight className="w-5 h-5" />
            </BrandBtn>
          )}
        </div>
      </div>
      <ProgressBar value={100} color="#22c55e" />
      <Style />
    </div>
  );

  // ===================== PERGUNTAS =====================
  const question = form.questions[currentStep];
  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-gray-950">
      {bgLayer}
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black/90" />
      {form.show_step_counter !== false && (
        <div className="relative z-10 px-6 pt-5 flex items-center justify-end">
          <span className="text-sm text-white/25 font-mono tracking-wider">{currentStep + 1} de {totalQ}</span>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center relative z-10 px-6">
        <div key={currentStep} className="w-full max-w-xl mx-auto animate-[slideIn_0.35s_ease-out]">
          <div className="flex items-center gap-2 mb-5">
            <span className="font-bold text-sm" style={{ color: C }}>{currentStep + 1}</span>
            <ChevronRight className="w-3 h-3 text-white/20" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-8 leading-tight tracking-tight">{question.question}</h2>

          {question.type === 'single_choice' ? (
            <div className="space-y-3">
              {(question.options || []).map((option, idx) => {
                const letter = String.fromCharCode(65 + idx);
                const selected = currentValue === option;
                return (
                  <button key={idx} onClick={() => {
                    setCurrentValue(option); const na = { ...answers, [question.id]: option }; setAnswers(na);
                    if (currentStep < totalQ - 1) setTimeout(() => setCurrentStep(p => p + 1), 250);
                    else setTimeout(() => submitForm(na), 250);
                  }} data-testid={`option-${idx}`}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 group ${
                      selected ? 'text-white scale-[1.01]' : 'border-white/10 bg-white/[0.04] text-white/80 hover:border-white/20 hover:bg-white/[0.08]'
                    }`} style={selected ? { borderColor: C, backgroundColor: C + '15' } : {}}>
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold border-2 flex-shrink-0 transition-all ${
                      selected ? 'text-white' : 'border-white/15 text-white/40 group-hover:border-white/30'
                    }`} style={selected ? { borderColor: C, backgroundColor: C } : {}}>
                      {letter}
                    </span>
                    <span className="text-base sm:text-lg font-medium">{option}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div>
              <input ref={inputRef} type={question.type === 'phone' ? 'tel' : 'text'}
                value={question.type === 'phone' ? formatPhone(currentValue) : currentValue}
                onChange={(e) => { setCurrentValue(question.type === 'phone' ? e.target.value.replace(/\D/g, '').slice(0, 11) : e.target.value); setError(''); }}
                onKeyDown={handleKeyDown}
                placeholder={question.type === 'phone' ? '(21) 99999-9999' : 'Digite sua resposta...'}
                data-testid="form-input"
                className="w-full bg-transparent border-b-2 border-white/12 text-white text-xl sm:text-2xl py-3 outline-none placeholder-white/20 transition-colors"
                onFocus={(e) => e.target.style.borderColor = C}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
              />
              {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
              <button onClick={handleNext} disabled={submitting} data-testid="continue-button"
                className="mt-8 inline-flex items-center gap-2 text-white font-bold text-base px-8 py-4 rounded-xl transition-all duration-200 hover:scale-[1.02] disabled:opacity-50"
                style={{ backgroundColor: C, boxShadow: `0 8px 20px ${Cglow}` }}
                onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = Chover)}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = C}>
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> :
                  currentStep === totalQ - 1 ? <><span>Enviar</span><Check className="w-5 h-5" /></> :
                  <><span>Continuar</span><ChevronRight className="w-5 h-5" /></>}
              </button>
            </div>
          )}
        </div>
      </div>
      <ProgressBar value={progress} color={C} />
      <Style />
    </div>
  );
};

const ProgressBar = ({ value, color }) => (
  <div className="h-1.5 bg-white/[0.04] w-full relative z-10">
    <div className="h-full transition-all duration-500 ease-out rounded-r-full" style={{ width: `${value}%`, backgroundColor: color }} />
  </div>
);
const Style = () => <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}@keyframes slideIn{from{opacity:0;transform:translateX(32px)}to{opacity:1;transform:translateX(0)}}`}</style>;

export default LeadForm;
